import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaSearch, FaChevronDown, FaTimes, FaCalendarAlt } from 'react-icons/fa';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getHospitalPayments } from '../services/subscriberWalletService';
import { useAuth } from '../auth/AuthContext';
import { formatDateTimeIST } from '../utils/dateFormatter';
import { HOSPITAL_PAYMENT_LIST_URL_KEY } from '../utils/subscriberHelpers';

const currencyRounded = (value) => {
  const num = Number(value);
  if (isNaN(num)) return '₹0';
  const truncated = Math.trunc(num * 100) / 100;
  const fixed = truncated.toFixed(2);
  const [wholePart, decimalPart] = fixed.split('.');
  const formattedWhole = Number(wholePart).toLocaleString('en-IN');
  if (decimalPart === '00') return `₹${formattedWhole}`;
  return `₹${formattedWhole}.${decimalPart}`;
};

const STATUS_OPTIONS = [
  { value: 'initiated', label: 'Initiated' },
  { value: 'success',   label: 'Success'   },
  { value: 'expired',   label: 'Expired'   },
  { value: 'complete',  label: 'Complete'  },
];

const getStatusBadgeStyle = (status) => {
  const statusLower = String(status || '').toLowerCase().trim();
  const statusColorMap = {
    'success':  { text: '#16A34A', bg: 'rgba(22, 163, 74, 0.1)' },
    'pending':  { text: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)' },
    'expired':  { text: '#6B7280', bg: 'rgba(107, 114, 128, 0.1)' },
    'initiated':{ text: '#3B82F6', bg: 'rgba(59, 130, 246, 0.1)' },
    'complete': { text: '#7C3AED', bg: 'rgba(124, 58, 237, 0.1)' },
  };
  const colorConfig = statusColorMap[statusLower] || statusColorMap['pending'];
  return {
    color: colorConfig.text,
    backgroundColor: colorConfig.bg,
    borderColor: colorConfig.text,
    minWidth: '90px',
    justifyContent: 'center',
  };
};

// Scroll offset saved when a row is opened, keyed by the list URL it was opened from,
// so Back lands on the same rows rather than the top of the page.
const SCROLL_KEY = 'hospitalPaymentList:scroll';

const HospitalPaymentList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const statusOptions = STATUS_OPTIONS;

  // Search
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const searchTimeoutRef = useRef(null);

  // Date filter
  const [dateFilter, setDateFilter] = useState(() => searchParams.get('date_filter') || '');
  const [startDate, setStartDate] = useState(() => searchParams.get('start_date') || '');
  const [endDate, setEndDate] = useState(() => searchParams.get('end_date') || '');
  const startDateObj = startDate ? new Date(startDate) : null;
  const endDateObj = endDate ? new Date(endDate) : null;
  const dateToString = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Custom DatePicker input
  const CustomDateInput = React.forwardRef(({ value, onClick, placeholder, clearDate }, ref) => (
    <div className="relative w-full">
      <button
        type="button"
        onClick={onClick}
        ref={ref}
        className="w-full px-3 py-1.5 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm text-left hover:border-gray-400 transition-colors"
      >
        <span className={value ? 'text-gray-900 block truncate pr-8' : 'text-gray-400 block truncate pr-8'}>
          {value || placeholder}
        </span>
      </button>
      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 pointer-events-none">
        {value && clearDate && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); clearDate(); }}
            className="text-gray-400 hover:text-gray-600 p-0.5 rounded hover:bg-gray-100 pointer-events-auto"
          >
            <FaTimes className="w-3 h-3" />
          </button>
        )}
        <FaCalendarAlt className="text-gray-400 w-4 h-4 pointer-events-none" />
      </div>
    </div>
  ));
  CustomDateInput.displayName = 'CustomDateInput';

  // Status multi-select
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const s = searchParams.get('status');
    return s ? s.split(',') : [];
  });
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  // Role
  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'SuperAdmin',
      'wallet_finance': 'Finance',
      'wallet_ops': 'Ops',
      'wallet_support': 'Support',
    };
    return roleMapping[authRole] || null;
  })();

  const canAccess = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const isOpsOrSuperAdmin = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  const [mobilesUnmasked, setMobilesUnmasked] = useState(false);

  const PAGE_SIZE = 25;
  const [page, setPage] = useState(() => parseInt(searchParams.get('page') || '1', 10));
  const [pagination, setPagination] = useState({ page: 1, items: PAGE_SIZE, total: 0, total_pages: 0 });

  const isInitialMountRef = useRef(true);
  const lastApiCallRef = useRef({});
  const isLoadingRef = useRef(false);

  const maskMobile = (value) => {
    if (!value) return 'N/A';
    const digits = String(value).replace(/\D/g, '');
    if (digits.length <= 4) return `******${digits}`;
    return `******${digits.slice(-4)}`;
  };

  // Sync URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    if (search.trim()) params.set('search', search.trim());
    if (dateFilter) params.set('date_filter', dateFilter);
    if (dateFilter === 'custom' && startDate) params.set('start_date', startDate);
    if (dateFilter === 'custom' && endDate) params.set('end_date', endDate);
    if (selectedStatuses.length > 0) params.set('status', selectedStatuses.join(','));
    setSearchParams(params, { replace: true });
  }, [page, search, dateFilter, startDate, endDate, selectedStatuses, setSearchParams]);

  const handleSearchSubmit = useCallback(() => {
    const val = searchInput.trim();
    if (val !== search) { setSearch(val); setPage(1); }
    if (searchTimeoutRef.current) { clearTimeout(searchTimeoutRef.current); searchTimeoutRef.current = null; }
  }, [searchInput, search]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(handleSearchSubmit, 2000);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchInput, handleSearchSubmit]);

  // Load data
  useEffect(() => {
    if (authLoading || !isAuthenticated || !currentRole) return;

    const apiParams = { page: page || 1, items: PAGE_SIZE };
    if (search.trim()) apiParams.search = search.trim();
    if (dateFilter) {
      apiParams.date_filter = dateFilter;
      if (dateFilter === 'custom' && startDate && endDate) {
        apiParams.start_date = startDate;
        apiParams.end_date = endDate;
      }
    }
    if (selectedStatuses.length > 0) apiParams.status = selectedStatuses;

    const apiCallKey = JSON.stringify(apiParams);
    if (lastApiCallRef.current.key === apiCallKey) return;
    if (isLoadingRef.current) return;
    lastApiCallRef.current = { key: apiCallKey };

    const load = async () => {
      isLoadingRef.current = true;
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('authToken');
        if (!token) { navigate('/login'); return; }

        const response = await getHospitalPayments(currentRole, apiParams);

        let items = null;
        let paginationData = null;

        if (response?.msg?.items && Array.isArray(response.msg.items)) {
          items = response.msg.items;
          paginationData = response.msg.pagination;
        } else if (response?.data?.items && Array.isArray(response.data.items)) {
          items = response.data.items;
          paginationData = response.data.pagination;
        } else if (Array.isArray(response)) {
          items = response;
        }

        if (items && Array.isArray(items)) {
          setBookings(items);
          if (paginationData) {
            setPagination({ ...paginationData, total_pages: Math.ceil(paginationData.total / paginationData.items) });
          } else {
            setPagination({ page: page || 1, items: PAGE_SIZE, total: items.length, total_pages: Math.ceil(items.length / PAGE_SIZE) });
          }
        } else {
          setBookings([]);
          setError(`Failed to load hospital payments`);
        }
      } catch {
        setError('Failed to load hospital payments');
        setBookings([]);
        setPagination({ page: 1, items: PAGE_SIZE, total: 0, total_pages: 0 });
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
        if (isInitialMountRef.current) isInitialMountRef.current = false;
      }
    };

    load();
  }, [currentRole, isAuthenticated, authLoading, navigate, page, search, dateFilter, startDate, endDate, selectedStatuses]);

  // Restore the scroll offset once the rows it was measured against are back.
  useEffect(() => {
    if (loading || bookings.length === 0) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
      if (saved) sessionStorage.removeItem(SCROLL_KEY);
      if (!saved || saved.search !== location.search) return;
      // The table settles over a few frames; keep nudging until the page is tall enough.
      let frames = 0;
      const restore = () => {
        window.scrollTo(0, saved.y);
        if (Math.abs(window.scrollY - saved.y) > 2 && frames++ < 30) requestAnimationFrame(restore);
      };
      requestAnimationFrame(restore);
    } catch {
      // sessionStorage unavailable — Back still keeps page and filters via the URL.
    }
  }, [loading, bookings, location.search]);

  const openBooking = (path, state) => {
    const from = `${location.pathname}${location.search}`;
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ search: location.search, y: window.scrollY }));
      sessionStorage.setItem(HOSPITAL_PAYMENT_LIST_URL_KEY, from);
    } catch {
      // ignore — scroll restore is a convenience
    }
    navigate(path, { state: { ...state, from } });
  };

  const handleStatusChange = (val) => {
    setSelectedStatuses(prev => prev.includes(val) ? prev.filter(s => s !== val) : [...prev, val]);
    setPage(1);
  };
  const clearStatusFilters = () => { setSelectedStatuses([]); setPage(1); };

  const handleDateFilterChange = (val) => {
    setDateFilter(val);
    setPage(1);
    if (val !== 'custom') { setStartDate(''); setEndDate(''); }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.status-dropdown-container')) setStatusDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!currentRole || !canAccess) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">This feature is only available for Ops and SuperAdmin roles.</p>
          <button onClick={() => navigate('/subscriber-wallet')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Go to Home</button>
        </div>
      </div>
    );
  }

  if (loading && bookings.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && bookings.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/subscriber-wallet')} className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600">
            ← Back to Home
          </button>
        </div>

        <div className="bg-white rounded-md shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Hospital Payments</h2>
            </div>

            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Name, Mobile, REF CODE or Hospital..."
                  className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearchSubmit(); } }}
                />
              </div>
            </div>

            {/* Filters */}
            <div className="space-y-4">
              {/* Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Filter</label>
                <div className="flex flex-wrap gap-2">
                  {[['', 'All Dates'], ['today', 'Today'], ['yesterday', 'Yesterday'], ['last_7_days', 'Last 7 Days'], ['custom', 'Custom Range']].map(([val, label]) => (
                    <button
                      key={val}
                      onClick={() => handleDateFilterChange(val)}
                      className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                        dateFilter === val
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-500'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {dateFilter === 'custom' && (() => {
                  const getMaxEndDate = () => { if (!startDateObj) return null; const d = new Date(startDateObj); d.setDate(d.getDate() + 31); return d; };
                  const getMinStartDate = () => { if (!endDateObj) return null; const d = new Date(endDateObj); d.setDate(d.getDate() - 31); return d; };
                  const validateRange = (s, e) => { if (s && e) return Math.ceil(Math.abs(e - s) / (1000 * 60 * 60 * 24)) <= 31; return true; };
                  return (
                    <div className="mt-3 flex gap-3">
                      <div className="w-48">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                        <DatePicker
                          selected={startDateObj}
                          onChange={(date) => {
                            if (!date) { setStartDate(''); setPage(1); return; }
                            if (!endDateObj || validateRange(date, endDateObj)) { setStartDate(dateToString(date)); setPage(1); }
                            else { const maxEnd = new Date(date); maxEnd.setDate(maxEnd.getDate() + 31); setStartDate(dateToString(date)); setEndDate(dateToString(maxEnd)); setPage(1); }
                          }}
                          selectsStart startDate={startDateObj} endDate={endDateObj}
                          maxDate={endDateObj || getMaxEndDate()} minDate={getMinStartDate()}
                          placeholderText="Select start date" dateFormat="dd/MM/yyyy"
                          customInput={<CustomDateInput clearDate={() => { setStartDate(''); setPage(1); }} />}
                          showYearDropdown showMonthDropdown dropdownMode="select"
                        />
                      </div>
                      <div className="w-48">
                        <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                        <DatePicker
                          selected={endDateObj}
                          onChange={(date) => {
                            if (!date) { setEndDate(''); setPage(1); return; }
                            if (!startDateObj || validateRange(startDateObj, date)) { setEndDate(dateToString(date)); setPage(1); }
                            else { const minStart = new Date(date); minStart.setDate(minStart.getDate() - 31); setStartDate(dateToString(minStart)); setEndDate(dateToString(date)); setPage(1); }
                          }}
                          selectsEnd startDate={startDateObj} endDate={endDateObj}
                          minDate={startDateObj} maxDate={getMaxEndDate()}
                          placeholderText="Select end date" dateFormat="dd/MM/yyyy"
                          customInput={<CustomDateInput clearDate={() => { setEndDate(''); setPage(1); }} />}
                          showYearDropdown showMonthDropdown dropdownMode="select"
                        />
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Status Filter */}
              <div className="status-dropdown-container w-full md:w-1/2">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  {selectedStatuses.length > 0 && (
                    <button onClick={clearStatusFilters} className="text-xs text-blue-600 hover:text-blue-800 font-medium">Clear All</button>
                  )}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                    className="w-full px-4 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between hover:border-gray-400 transition-colors"
                  >
                    <span className="text-sm text-gray-700">
                      {selectedStatuses.length === 0
                        ? 'All Status'
                        : selectedStatuses.length === 1
                        ? statusOptions.find(o => o.value === selectedStatuses[0])?.label || '1 selected'
                        : `${selectedStatuses.length} selected`}
                    </span>
                    <FaChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${statusDropdownOpen ? 'transform rotate-180' : ''}`} />
                  </button>
                  {statusDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      {statusOptions.length === 0 ? (
                        <div className="px-4 py-2 text-sm text-gray-500">No options available</div>
                      ) : (
                        statusOptions.map(opt => (
                          <label key={opt.value} className="flex items-center px-4 py-2 hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedStatuses.includes(opt.value)}
                              onChange={() => handleStatusChange(opt.value)}
                              className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-700">{opt.label}</span>
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>
                {selectedStatuses.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedStatuses.map(val => {
                      const opt = statusOptions.find(o => o.value === val);
                      return opt ? (
                        <span key={val} className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-md">
                          {opt.label}
                          <button type="button" onClick={() => handleStatusChange(val)} className="ml-1.5 text-blue-600 hover:text-blue-800">
                            <FaTimes className="w-3 h-3" />
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <span>Mobile</span>
                      {isOpsOrSuperAdmin && (
                        <button
                          type="button"
                          onClick={() => setMobilesUnmasked(v => !v)}
                          className="inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
                          title={mobilesUnmasked ? 'Mask mobiles' : 'Unmask mobiles'}
                        >
                          {mobilesUnmasked ? <FaEyeSlash className="w-3 h-3" /> : <FaEye className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">REF CODE</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hospital</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cashback</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">EC</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">RC</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WB</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bookings.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-6 py-8 text-center text-gray-500">
                      {loading ? 'Loading...' : 'No hospital payments found'}
                    </td>
                  </tr>
                ) : (
                  bookings.map((item) => {
                    const mobileDisplay = !mobilesUnmasked
                      ? maskMobile(item.mobile_number)
                      : (item.mobile_number || 'N/A');
                    return (
                      <tr
                        key={item.booking_id || item.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => openBooking(
                          `/subscriber-wallet/hospital-booking/${encodeURIComponent(item.application_id)}?booking_id=${encodeURIComponent(item.booking_id || item.id)}`,
                          { state: { booking: item } }
                        )}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateTimeIST(item.date_time)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.subscriber_name || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{mobileDisplay}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.ref_code || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.hospital_name || 'N/A'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {currencyRounded(item.price || item.amount || 0)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{currencyRounded(item.cashback || 0)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{currencyRounded(item.eligible_cashback || 0)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{currencyRounded(item.redeemable_cashback || 0)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{currencyRounded(item.wallet_balance || 0)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className="inline-flex px-2 py-1 text-xs font-semibold rounded-full border justify-center"
                            style={getStatusBadgeStyle(item.booking_status || item.status)}
                          >
                            {item.booking_status || item.status || 'N/A'}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.total > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {((pagination.page - 1) * pagination.items) + 1}–{Math.min(pagination.page * pagination.items, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  disabled={pagination.page === 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                >
                  Prev
                </button>
                {Array.from({ length: pagination.total_pages || 1 }).map((_, i) => {
                  const pageNum = i + 1;
                  const totalPages = pagination.total_pages || 1;
                  const shouldShow = pageNum === 1 || pageNum === totalPages || (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2);
                  if (!shouldShow) {
                    if (pageNum === pagination.page - 3 || pageNum === pagination.page + 3) return <span key={`e-${i}`} className="px-2 text-gray-400">...</span>;
                    return null;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1 border rounded-md text-sm ${pagination.page === pageNum ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  disabled={pagination.page >= (pagination.total_pages || 1)}
                  onClick={() => setPage(p => Math.min(pagination.total_pages || 1, p + 1))}
                  className={`px-3 py-1 border rounded-md text-sm ${pagination.page >= (pagination.total_pages || 1) ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HospitalPaymentList;
